import { fromJS, List, Map } from 'immutable';
import { camelize } from 'humps';

import * as ActionType from '../actions/directorDashboard';

const indexRolesByTitle = roles => Map(roles.map(role => [role.get('titleId'), role]));

/**
 * Merge the Firm-Wide and Module level benchmark sets into a single set used by the Benchmarks tab.
 * The Module level set is the active one when present, otherwise the Firm-Wide one.
 * Every metric gets `firmWideBenchmark` (Firm-Wide `benchmark`) and `moduleAvg` (Module `avg`, `0` when the level is missing).
 *
 * @param {Object} data Response data with `firmWide` and optional `module` benchmark sets.
 */
const normalizeBenchmarks = data => {
  const firmWideLevel = fromJS(data?.firmWide ?? {});
  const moduleLevel = data?.module ? fromJS(data.module) : null;
  const activeLevel = moduleLevel ?? firmWideLevel;
  const firmWideRoles = indexRolesByTitle(firmWideLevel.get('roles', List()));
  const moduleRoles = indexRolesByTitle(moduleLevel?.get('roles', List()) ?? List());

  return activeLevel.update('roles', List(), roles =>
    roles.map(role => {
      const titleId = role.get('titleId');

      return role.update('metrics', Map(), metrics =>
        metrics.map((metric, metricKey) =>
          metric.merge({
            firmWideBenchmark: firmWideRoles.getIn([titleId, 'metrics', metricKey, 'benchmark'], 0),
            moduleAvg: moduleRoles.getIn([titleId, 'metrics', metricKey, 'avg'], 0),
          }),
        ),
      );
    }),
  );
};

const defaultState = fromJS({
  statistics: [],
  statisticsLoading: false,
  benchmarks: [],
  benchmarksFilled: [],
  benchmarksLoading: false,
  directors: [],
  directorsLoading: false,
  directorTeams: [],
  directorTeamsLoading: false,
  isCreatingTeam: false,
  userTitles: [],
  userTitlesLoading: false,
  users: [],
  usersLoading: false,
  teamDeleting: false,
  projects: [],
  projectsLoading: false,
  projectsMeta: {},
  project: {},
  projectLoading: false,
  projectStatistics: [],
  projectStatisticsLoading: false,
});

export default function reducer(state = defaultState, action) {
  switch (action.type) {
    case ActionType.LOADING_STATISTICS:
      return state.set('statisticsLoading', true);

    case ActionType.LOADING_STATISTICS_SUCCESS:
      return state.merge({
        statisticsLoading: false,
        statistics: action.response.data,
      });

    case ActionType.LOADING_STATISTICS_FAILURE:
      return state.set('statisticsLoading', false);

    case ActionType.LOADING_BENCHMARKS:
      return state.set('benchmarksLoading', true);

    case ActionType.LOADING_BENCHMARKS_SUCCESS: {
      const benchmarks = normalizeBenchmarks(action.response.data);

      return state.merge({
        benchmarksLoading: false,
        benchmarks,
        benchmarksFilled: benchmarks,
      });
    }

    case ActionType.LOADING_BENCHMARKS_FAILURE:
      return state.set('benchmarksLoading', false);

    case ActionType.CHANGE_BENCHMARKS_ROLE_FORM: {
      const roles = state.getIn(['benchmarks', 'roles']);
      const roleIndex = roles.findIndex(role => role.get('titleId') === action.id);

      if (roleIndex === -1) return state;

      const metricKey = camelize(action.key);

      return state.setIn(['benchmarks', 'roles', roleIndex, 'metrics', metricKey, 'benchmark'], Number(action.value));
    }

    case ActionType.CHANGE_BENCHMARKS_TEAM_FORM: {
      const teams = state.getIn(['benchmarks', 'teams']);
      const metricKey = camelize(action.key);

      const teamIndex = teams.findIndex(team => team.get('children')?.some(child => child.get('teamId') === action.id));

      if (teamIndex === -1) return state;

      const childIndex = teams.getIn([teamIndex, 'children']).findIndex(child => child.get('teamId') === action.id);

      return state.setIn(
        ['benchmarks', 'teams', teamIndex, 'children', childIndex, 'metrics', metricKey, 'benchmark'],
        Number(action.value),
      );
    }

    case ActionType.AUTOFILL_BENCHMARKS:
      return state.updateIn(['benchmarks', 'roles'], roles =>
        roles.map(role =>
          role.update('metrics', metrics =>
            metrics.map(metric => metric.set('benchmark', Number(metric.get(action.averageKey) ?? 0))),
          ),
        ),
      );

    case ActionType.SAVE_BENCHMARKS:
      return state.set('benchmarksLoading', true);

    case ActionType.SAVE_BENCHMARKS_SUCCESS:

    // falls through
    case ActionType.SAVE_BENCHMARKS_FAILURE:
      return state.set('benchmarksLoading', false);

    case ActionType.RESET_BENCHMARKS_FORM:
      return state.set('benchmarks', state.get('benchmarksFilled'));

    case ActionType.LOADING_DIRECTORS:
      return state.set('directorsLoading', true);

    case ActionType.LOADING_DIRECTORS_SUCCESS:
      return state.merge({
        directorsLoading: false,
        directors: action.response.data,
      });

    case ActionType.LOADING_DIRECTORS_FAILURE:
      return state.set('directorsLoading', false);

    case ActionType.LOADING_DIRECTOR_TEAMS:
      return state.set('directorTeamsLoading', true);

    case ActionType.LOADING_DIRECTOR_TEAMS_SUCCESS:
      return state.merge({
        directorTeamsLoading: false,
        directorTeams: action.response.data,
      });

    case ActionType.LOADING_DIRECTOR_TEAMS_FAILURE:
      return state.set('directorTeamsLoading', false);

    case ActionType.CREATE_TEAM:
      return state.set('isCreatingTeam', true);

    case ActionType.CREATE_TEAM_SUCCESS:
      return state.merge({
        isCreatingTeam: false,
        directorTeams: action.response.data,
      });

    case ActionType.CREATE_TEAM_FAILURE:
      return state.set('isCreatingTeam', false);

    case ActionType.LOADING_USER_TITLES:
      return state.set('userTitlesLoading', true);

    case ActionType.LOADING_USERS:
      return state.set('usersLoading', true);

    case ActionType.LOADING_USERS_SUCCESS:
      return state.merge({
        usersLoading: false,
        users: action.response.data,
      });

    case ActionType.LOADING_USERS_FAILURE:
      return state.set('usersLoading', false);

    case ActionType.LOADING_USER_TITLES_SUCCESS:
      return state.merge({
        userTitlesLoading: false,
        userTitles: action.response.data,
      });

    case ActionType.LOADING_USER_TITLES_FAILURE:
      return state.set('userTitlesLoading', false);

    case ActionType.LOADING_PROJECTS:
      return state.set('projectsLoading', true);

    case ActionType.LOADING_PROJECTS_SUCCESS:
      return state.merge({
        projectsLoading: false,
        projects: action.page > 1 ? state.get('projects').concat(fromJS(action.response.data)) : action.response.data,
        projectsMeta: action.response.meta,
      });

    case ActionType.LOADING_PROJECTS_FAILURE:
      return state.set('projectsLoading', false);

    case ActionType.LOADING_PROJECT:
      return state.set('projectLoading', true);

    case ActionType.LOADING_PROJECT_SUCCESS: {
      const nextState = state.merge({
        projectLoading: false,
        project: action.response.data,
      });

      const projectIndex = state.get('projects').findIndex(project => project.get('id') === action.response.data.id);

      if (projectIndex === -1) return nextState;

      return nextState.setIn(['projects', projectIndex], fromJS(action.response.data));
    }

    case ActionType.LOADING_PROJECT_FAILURE:
      return state.set('projectLoading', false);

    case ActionType.LOADING_PROJECT_STATISTICS:
      return state.set('projectStatisticsLoading', true);

    case ActionType.LOADING_PROJECT_STATISTICS_SUCCESS:
      return state.merge({
        projectStatisticsLoading: false,
        projectStatistics: action.response.data,
      });

    case ActionType.LOADING_PROJECT_STATISTICS_FAILURE:
      return state.set('projectStatisticsLoading', false);

    default:
      return state;
  }
}
